import { useLayoutEffect, useRef, type DependencyList, type RefObject } from "react";

interface UseFlipResizeOptions {
  /** Element whose box animates from its previous measured size to the new one. */
  elementRef: RefObject<HTMLElement | null>;
  /** Inner content, pinned to its post-render natural size so it holds the FINAL layout
   *  (no reflow / ellipsis) while the outer box is snapped smaller and grows back. */
  contentRef: RefObject<HTMLElement | null>;
  /** A remeasure-and-animate pass runs whenever these change. */
  deps: DependencyList;
  durationMs: number;
  easing: string;
  /** `"width"` for a content-driven width; `"both"` when height changes too. Defaults to `"both"`. */
  axes?: "width" | "both";
}

/**
 * FLIP resize: on a size-affecting change, snap the element back to its previous measured size with
 * no transition, then transition to the new natural size on the next frame. CSS cannot transition to
 * `auto`, so an element sized to its content can only animate this way. Honors
 * `prefers-reduced-motion` by jumping straight to the new size.
 */
export function useFlipResize({
  elementRef,
  contentRef,
  deps,
  durationMs,
  easing,
  axes = "both",
}: UseFlipResizeOptions) {
  const prevRectRef = useRef<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const el = elementRef.current;
    const inner = contentRef.current;
    if (!el || !inner) return;

    const animateHeight = axes === "both";
    const rect = el.getBoundingClientRect();
    const prev = prevRectRef.current;
    prevRectRef.current = { width: rect.width, height: rect.height };

    if (!prev) return;
    const widthChanged = Math.abs(prev.width - rect.width) >= 0.5;
    const heightChanged = animateHeight && Math.abs(prev.height - rect.height) >= 0.5;
    if (!widthChanged && !heightChanged) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const innerRect = inner.getBoundingClientRect();
    inner.style.width = `${innerRect.width}px`;
    if (animateHeight) inner.style.height = `${innerRect.height}px`;
    inner.style.flex = "0 0 auto";

    el.style.overflow = "hidden";
    el.style.transition = "none";
    el.style.width = `${prev.width}px`;
    if (animateHeight) el.style.height = `${prev.height}px`;
    void el.offsetHeight; // commit the "from" size before the transition registers

    const raf = requestAnimationFrame(() => {
      const parts = [`width ${durationMs}ms ${easing}`];
      if (animateHeight) parts.push(`height ${durationMs}ms ${easing}`);
      el.style.transition = parts.join(", ");
      el.style.width = `${rect.width}px`;
      if (animateHeight) el.style.height = `${rect.height}px`;
    });

    let cleared = false;
    const clear = () => {
      if (cleared) return;
      cleared = true;
      el.style.width = "";
      el.style.height = "";
      el.style.transition = "";
      el.style.overflow = "";
      inner.style.width = "";
      inner.style.height = "";
      inner.style.flex = "";
      el.removeEventListener("transitionend", onEnd);
    };
    const onEnd = (evt: TransitionEvent) => {
      if (evt.target !== el) return;
      if (evt.propertyName !== "width" && evt.propertyName !== "height") return;
      clear();
    };
    el.addEventListener("transitionend", onEnd);
    const timeout = window.setTimeout(clear, durationMs + 100); // safety net if transitionend never fires

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timeout);
      clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
